/**
 * FiltersStep - Step 4: Configure default filters
 *
 * Features:
 * - Add/remove filter rows
 * - Field selector (from selected fields)
 * - Operator selector (type-aware)
 * - Value input (type-specific)
 * - "Locked" toggle (immutable by users)
 */

import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  IconButton,
  Select,
  Option,
  Input,
  Alert,
  Sheet,
  Chip,
  Tooltip,
  FormControl,
  FormLabel,
} from "@mui/joy";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { useViewBuilderContext } from "../ViewBuilderContext";
import {
  FILTER_OPERATORS_BY_TYPE,
  VIEW_DEFAULT_FILTER_OPERATORS,
} from "../../../types";
import type {
  ViewBuilderFilter,
  FilterOperator,
  SchemaColumnResponse,
} from "../../../types";

interface FiltersStepProps {
  workspaceId: string;
}

// Operator display names
const OPERATOR_LABELS: Record<FilterOperator, string> = {
  eq: "Equals",
  ne: "Not equals",
  gt: "Greater than",
  gte: "Greater or equal",
  lt: "Less than",
  lte: "Less or equal",
  like: "Pattern match (LIKE)",
  ilike: "Pattern match (case-insensitive)",
  contains: "Contains",
  not_contains: "Does not contain",
  starts_with: "Starts with",
  ends_with: "Ends with",
  in: "In list",
  not_in: "Not in list",
  between: "Between",
  is_null: "Is empty",
  is_not_null: "Is not empty",
};

export default function FiltersStep({ workspaceId }: FiltersStepProps) {
  const { state, addFilter, updateFilter, removeFilter } =
    useViewBuilderContext();

  // Build field options from selected fields
  const fieldOptions = useMemo(() => {
    return state.selectedFields.map((field) => ({
      id: field.id,
      columnId: field.columnId,
      tableAlias: field.tableAlias,
      label: `${field.tableAlias || field.tableName}.${field.displayLabel}`,
      column: field.column,
    }));
  }, [state.selectedFields]);

  // Get available operators for a column's data type
  const getOperatorsForColumn = (
    column: SchemaColumnResponse,
  ): FilterOperator[] => {
    const baseType = column.data_type.toLowerCase();
    return (
      FILTER_OPERATORS_BY_TYPE[baseType] || FILTER_OPERATORS_BY_TYPE["string"]
    ).filter((operator) => VIEW_DEFAULT_FILTER_OPERATORS.includes(operator));
  };

  // Handle adding a new filter
  const handleAddFilter = () => {
    if (fieldOptions.length === 0) return;

    const firstField = fieldOptions[0];
    const operators = getOperatorsForColumn(firstField.column);
    addFilter(firstField.columnId, firstField.tableAlias, operators[0] || "eq");
  };

  // Handle field change for a filter
  const handleFieldChange = (filterId: string, columnId: string) => {
    const field = fieldOptions.find((f) => f.columnId === columnId);
    if (!field) return;

    const operators = getOperatorsForColumn(field.column);
    const currentFilter = state.filters.find((f) => f.id === filterId);

    // Reset operator if current one isn't valid for new field type
    const newOperator =
      currentFilter && operators.includes(currentFilter.operator)
        ? currentFilter.operator
        : operators[0];

    updateFilter(filterId, {
      columnId: field.columnId,
      tableAlias: field.tableAlias,
      operator: newOperator,
      value: null, // Reset value when field changes
    });
  };

  // Handle operator change
  const handleOperatorChange = (filterId: string, operator: FilterOperator) => {
    // Reset value for certain operators
    const needsNoValue = operator === "is_null" || operator === "is_not_null";
    updateFilter(filterId, {
      operator,
      value: needsNoValue
        ? null
        : state.filters.find((f) => f.id === filterId)?.value,
    });
  };

  // Handle value change
  const handleValueChange = (filterId: string, value: unknown) => {
    updateFilter(filterId, { value });
  };

  // Handle locked toggle
  const handleLockedToggle = (filterId: string) => {
    const filter = state.filters.find((f) => f.id === filterId);
    if (filter) {
      updateFilter(filterId, { isLocked: !filter.isLocked });
    }
  };

  // Get the column for a filter
  const getColumnForFilter = (
    filter: ViewBuilderFilter,
  ): SchemaColumnResponse | undefined => {
    const field = state.selectedFields.find(
      (f) =>
        f.columnId === filter.columnId && f.tableAlias === filter.tableAlias,
    );
    return field?.column;
  };

  // Render value input based on column type and operator
  const renderValueInput = (filter: ViewBuilderFilter) => {
    const column = getColumnForFilter(filter);
    if (!column) return null;

    // No value needed for null checks
    if (filter.operator === "is_null" || filter.operator === "is_not_null") {
      return (
        <Typography
          level="body-sm"
          sx={{ color: "text.tertiary", fontStyle: "italic" }}
        >
          No value needed
        </Typography>
      );
    }

    const dataType = column.data_type.toLowerCase();

    // Boolean type
    if (dataType === "boolean") {
      return (
        <Select
          size="sm"
          value={String(filter.value ?? "")}
          onChange={(_, v) => handleValueChange(filter.id, v === "true")}
          sx={{ minWidth: 100 }}
        >
          <Option value="true">True</Option>
          <Option value="false">False</Option>
        </Select>
      );
    }

    // Number types
    if (dataType === "integer" || dataType === "decimal") {
      if (filter.operator === "between") {
        const rangeValue =
          (filter.value as { min?: number; max?: number }) || {};
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Input
              size="sm"
              type="number"
              placeholder="Min"
              value={rangeValue.min ?? ""}
              onChange={(e) =>
                handleValueChange(filter.id, {
                  ...rangeValue,
                  min: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              sx={{ width: 80 }}
            />
            <Typography level="body-sm">to</Typography>
            <Input
              size="sm"
              type="number"
              placeholder="Max"
              value={rangeValue.max ?? ""}
              onChange={(e) =>
                handleValueChange(filter.id, {
                  ...rangeValue,
                  max: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              sx={{ width: 80 }}
            />
          </Stack>
        );
      }

      return (
        <Input
          size="sm"
          type="number"
          placeholder="Enter value"
          value={
            typeof filter.value === "number"
              ? filter.value
              : ((filter.value as string) ?? "")
          }
          onChange={(e) =>
            handleValueChange(
              filter.id,
              e.target.value ? Number(e.target.value) : null,
            )
          }
          sx={{ minWidth: 120 }}
        />
      );
    }

    // Date types
    if (dataType === "date" || dataType === "datetime") {
      if (filter.operator === "between") {
        const rangeValue =
          (filter.value as { start?: string; end?: string }) || {};
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Input
              size="sm"
              type={dataType === "datetime" ? "datetime-local" : "date"}
              value={rangeValue.start ?? ""}
              onChange={(e) =>
                handleValueChange(filter.id, {
                  ...rangeValue,
                  start: e.target.value,
                })
              }
              sx={{ width: 160 }}
            />
            <Typography level="body-sm">to</Typography>
            <Input
              size="sm"
              type={dataType === "datetime" ? "datetime-local" : "date"}
              value={rangeValue.end ?? ""}
              onChange={(e) =>
                handleValueChange(filter.id, {
                  ...rangeValue,
                  end: e.target.value,
                })
              }
              sx={{ width: 160 }}
            />
          </Stack>
        );
      }

      return (
        <Input
          size="sm"
          type={dataType === "datetime" ? "datetime-local" : "date"}
          value={(filter.value as string) ?? ""}
          onChange={(e) => handleValueChange(filter.id, e.target.value)}
          sx={{ minWidth: 160 }}
        />
      );
    }

    // In/Not in operators - comma separated values
    if (filter.operator === "in" || filter.operator === "not_in") {
      return (
        <Input
          size="sm"
          placeholder="value1, value2, ..."
          value={
            Array.isArray(filter.value)
              ? filter.value.join(", ")
              : String(filter.value ?? "")
          }
          onChange={(e) => {
            const values = e.target.value
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean);
            handleValueChange(filter.id, values.length > 0 ? values : null);
          }}
          sx={{ minWidth: 200 }}
        />
      );
    }

    // Default: string input
    return (
      <Input
        size="sm"
        placeholder="Enter value"
        value={(filter.value as string) ?? ""}
        onChange={(e) => handleValueChange(filter.id, e.target.value || null)}
        sx={{ minWidth: 150 }}
      />
    );
  };

  if (state.selectedFields.length === 0) {
    return (
      <Alert color="warning">
        Please select at least one field in Step 3 before configuring filters.
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Info Card */}
      <Card variant="soft" color="primary">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center">
            <FilterListIcon />
            <Box>
              <Typography level="title-sm">Default Filters</Typography>
              <Typography level="body-xs">
                Configure filters that will be applied by default when viewing
                this data. Locked filters cannot be changed by users.
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Filters List */}
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Typography level="title-lg">Filters</Typography>
            <Chip size="sm" variant="soft">
              {state.filters.length}
            </Chip>
            <Box sx={{ flex: 1 }} />
            <Button
              size="sm"
              variant="soft"
              startDecorator={<AddIcon />}
              onClick={handleAddFilter}
              disabled={fieldOptions.length === 0}
            >
              Add Filter
            </Button>
          </Stack>

          {state.filters.length === 0 ? (
            <Box
              sx={{
                py: 4,
                textAlign: "center",
                color: "text.tertiary",
              }}
            >
              <FilterListIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
              <Typography level="body-sm">
                No filters configured. Click "Add Filter" to create one.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {state.filters.map((filter, index) => {
                const column = getColumnForFilter(filter);
                const operators = column ? getOperatorsForColumn(column) : [];

                return (
                  <Sheet
                    key={filter.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: "md",
                      borderColor: filter.isLocked
                        ? "warning.300"
                        : "neutral.200",
                      bgcolor: filter.isLocked
                        ? "warning.50"
                        : "background.surface",
                    }}
                  >
                    <Stack spacing={2}>
                      {/* Filter Row */}
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        alignItems={{ xs: "stretch", md: "flex-end" }}
                      >
                        {/* Field Select */}
                        <FormControl sx={{ minWidth: 200 }}>
                          <FormLabel>Field</FormLabel>
                          <Select
                            size="sm"
                            value={filter.columnId}
                            onChange={(_, v) =>
                              v && handleFieldChange(filter.id, v)
                            }
                          >
                            {fieldOptions.map((opt) => (
                              <Option
                                key={`${opt.columnId}-${opt.tableAlias}`}
                                value={opt.columnId}
                              >
                                {opt.label}
                              </Option>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Operator Select */}
                        <FormControl sx={{ minWidth: 160 }}>
                          <FormLabel>Operator</FormLabel>
                          <Select
                            size="sm"
                            value={filter.operator}
                            onChange={(_, v) =>
                              v &&
                              handleOperatorChange(
                                filter.id,
                                v as FilterOperator,
                              )
                            }
                          >
                            {operators.map((op) => (
                              <Option key={op} value={op}>
                                {OPERATOR_LABELS[op] || op}
                              </Option>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Value Input */}
                        <FormControl sx={{ flex: 1 }}>
                          <FormLabel>Value</FormLabel>
                          {renderValueInput(filter)}
                        </FormControl>

                        {/* Actions */}
                        <Stack direction="row" spacing={1}>
                          <Tooltip
                            title={
                              filter.isLocked
                                ? "Unlock (users can change)"
                                : "Lock (users cannot change)"
                            }
                          >
                            <IconButton
                              size="sm"
                              variant={filter.isLocked ? "soft" : "plain"}
                              color={filter.isLocked ? "warning" : "neutral"}
                              onClick={() => handleLockedToggle(filter.id)}
                            >
                              {filter.isLocked ? (
                                <LockIcon fontSize="small" />
                              ) : (
                                <LockOpenIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Remove filter">
                            <IconButton
                              size="sm"
                              variant="soft"
                              color="danger"
                              onClick={() => removeFilter(filter.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>

                      {/* Filter summary */}
                      <Typography
                        level="body-xs"
                        sx={{ color: "text.tertiary" }}
                      >
                        {index + 1}.{" "}
                        {
                          fieldOptions.find(
                            (f) => f.columnId === filter.columnId,
                          )?.label
                        }{" "}
                        <strong>
                          {OPERATOR_LABELS[filter.operator]?.toLowerCase()}
                        </strong>
                        {filter.value !== null &&
                          filter.operator !== "is_null" &&
                          filter.operator !== "is_not_null" && (
                            <>
                              {" "}
                              <Chip size="sm" variant="soft">
                                {typeof filter.value === "object"
                                  ? JSON.stringify(filter.value)
                                  : String(filter.value)}
                              </Chip>
                            </>
                          )}
                        {filter.isLocked && (
                          <Chip
                            size="sm"
                            variant="soft"
                            color="warning"
                            sx={{ ml: 1 }}
                          >
                            Locked
                          </Chip>
                        )}
                      </Typography>
                    </Stack>
                  </Sheet>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
